import { Controller } from '@hotwired/stimulus'
import Swiper from 'swiper/bundle'
import debounce from 'lodash/debounce'
import { get } from '@rails/request.js'
import 'swiper/css/bundle'

export default class extends Controller {
  static targets = [
    'main',
    'thumbnails',
    'previous',
    'next',
    'slideshowControls',
    'fullscreenContainer',
    'fullscreenImage',
    'swiperUpdater',
    'swiperFinisher'
  ]

  static values = {
    initialSlideIndex: { type: Number, default: 0 },
    initialPage: { type: Number, default: 1 },
    initialPictureId: String,
    url: String
  }

  connect() {
    this.scrollingParent = this.element.closest('.fixed') || document.body
    this.modal = this.element.closest('[data-controller="pictures--slideshow-modal"]')
    this.prevPage = this.initialPageValue - 1
    this.nextPage = this.initialPageValue + 1
    this.finished = false
    this.detailsPictureId = this.initialPictureIdValue

    setTimeout(() => this.init(), this.modal ? 100 : 0)
  }

  init() {
    this.modal?.scrollTo(0, 0)
    this.mountMainSwiper()
    this.mountThumbnailsSwiper()
    this.linkSwipers()
  }

  mountMainSwiper() {
    this.mainSwiper = new Swiper(this.mainTarget, {
      init: false,
      runCallbacksOnInit: false,
      slidesPerView: 1,
      centeredSlides: true,
      spaceBetween: 10,
      navigation: { nextEl: this.nextTarget, prevEl: this.previousTarget, disabledClass: 'disabled' },
      keyboard: { enabled: true, onlyInViewport: true }
    })

    this.mainSwiper.on('init', async () => {
      setTimeout(async () => {
        await this.fetchPage(0)
        this.updateActiveThumbnailClasses()
        this.mainTarget.classList.remove('opacity-0')
      }, 100)
    })
    this.mainSwiper.on(
      'slideChange',
      debounce(() => this.fetchPicture(), 250)
    )
    this.mainSwiper.on('reachBeginning', () => this.fetchPage(-1))
    this.mainSwiper.on('reachEnd', () => this.fetchPage(1))
    this.mainSwiper.init()
  }

  mountThumbnailsSwiper() {
    this.thumbnailsSwiper = new Swiper(this.thumbnailsTarget, {
      init: false,
      runCallbacksOnInit: false,
      centeredSlides: true,
      slideToClickedSlide: true,
      slidesPerView: 1,
      breakpoints: { 370: { slidesPerView: 3 }, 768: { slidesPerView: 5 } }
    })

    this.thumbnailsSwiper.on('activeIndexChange', () => this.updateActiveThumbnailClasses())
    this.thumbnailsSwiper.init()
  }

  linkSwipers() {
    this.mainSwiper.controller.control = this.thumbnailsSwiper
    this.thumbnailsSwiper.controller.control = this.mainSwiper
  }

  updateActiveThumbnailClasses() {
    this.thumbnailsSwiper.el.querySelector('img.ring-2.ring-white')?.classList.remove('ring-2', 'ring-white')
    this.thumbnailsSwiper.slides[this.thumbnailsSwiper.activeIndex]
      .querySelector('img')
      .classList.add('ring-2', 'ring-white')
  }

  async fetchPicture() {
    const pictureId = this.mainSwiper.slides[this.mainSwiper.activeIndex].dataset.id
    if (this.detailsPictureId === pictureId) return

    const url = this.mainSwiper.slides[this.mainSwiper.activeIndex].dataset.url
    await get(url, { responseKind: 'turbo-stream', query: this.modal ? { modal: true } : {} })
    window.history.pushState({}, '', url)
    this.detailsPictureId = pictureId
  }

  async fetchPage(dir) {
    if (dir !== 0 && this.mainSwiper.slides.length === 1) return

    const page = this.pageFromDirection(dir)
    if (page === 0 || this.finished || !this.urlValue) return

    const query = { page }
    if (dir === 0) {
      query.picture_id = this.initialPictureIdValue
    } else {
      query.dir = dir
    }
    if (this.modal) query.modal = true
    await get(this.urlValue, { responseKind: 'turbo-stream', query })

    if (dir > 0) this.nextPage++
    if (dir < 0) this.prevPage--
  }

  pageFromDirection(dir) {
    if (dir === 0) return this.initialPageValue
    else if (dir > 0) return this.nextPage
    else return this.prevPage
  }

  swiperUpdaterTargetConnected() {
    const activeSlide = this.mainSwiper.slides[this.mainSwiper.activeIndex]
    this.mainSwiper.update()
    this.thumbnailsSwiper.update()

    const index = this.mainSwiper.slides.indexOf(activeSlide)
    this.mainSwiper.slideTo(index, 0, false)
    this.thumbnailsSwiper.slideTo(index, 0, false)

    this.swiperUpdaterTarget.remove()

    if (index === 0) this.mainSwiper.emit('reachBeginning')
    else if (index === this.mainSwiper.slides.length - 1) this.mainSwiper.emit('reachEnd')

    setTimeout(() => {
      this.slideshowControlsTarget.classList.remove('opacity-0')
    }, 500)
  }

  swiperFinisherTargetConnected() {
    this.finished = true
    this.swiperFinisherTarget.remove()
  }

  toggleFullscreen(event) {
    event.preventDefault()
    const imageTag = this.mainSwiper.slides[this.mainSwiper.activeIndex].querySelector('img')
    this.fullscreenImageTarget.src = imageTag.srcset || imageTag.src
    this.scrollingParent.classList.toggle('overflow-hidden')
    this.fullscreenContainerTarget.classList.toggle('hidden')
  }
}
